// LATERAL / APPLY frame provenance + parent-scope filtering.
//
// A LATERAL (postgres / mysql) or CROSS|OUTER APPLY (mssql / oracle) derived
// table may reference FROM items declared BEFORE it in the same FROM list, but
// NOT ones declared after it (SQL scoping rule). A plain, non-lateral subquery —
// a scalar subquery in the SELECT list, a `WHERE x IN (…)` subquery, a bare
// derived table — may correlate to ANY sibling in the enclosing query, so this
// ordering restriction is applied ONLY to lateral/apply frames (decision 3).
//
// Detection is a bounded, trivia-safe token walk-back from the frame's opening
// `(`: if the immediately-preceding keyword is LATERAL or APPLY (APPLY covers
// both CROSS APPLY and OUTER APPLY, which both end in APPLY), the frame is
// lateral/apply. A miss (e.g. a rare line comment wedged between the keyword and
// the `(`) simply leaves the frame untagged → whole-parent visibility, the safe
// over-suggest direction that matches pre-fix behavior.
//
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Scope Resolver.

import type { Scope } from '../types';

const WORD = /[A-Za-z0-9_]/;

export function frameIsLateralOrApply(
  source: string,
  openOffset: number,
  floor: number,
): boolean {
  const wordEnd = skipTriviaBackward(source, openOffset, floor);
  let s = wordEnd;
  while (s > floor && WORD.test(source[s - 1])) s--;
  if (s === wordEnd) return false;
  const word = source.slice(s, wordEnd).toUpperCase();
  return word === 'LATERAL' || word === 'APPLY';
}

// Skip whitespace + block comments backward from `from` (exclusive), bounded at
// `floor`. Returns the index one-past the nearest preceding non-trivia char.
// Line comments between the keyword and the `(` are deliberately NOT unwound (a
// rare shape); a miss there degrades to the safe over-suggest direction.
function skipTriviaBackward(source: string, from: number, floor: number): number {
  let i = from;
  for (;;) {
    while (i > floor && /\s/.test(source[i - 1])) i--;
    // Block comment `… */` — jump back to its opening `/*`.
    if (i - 2 >= floor && source[i - 1] === '/' && source[i - 2] === '*') {
      let p = i - 3;
      while (p > floor && !(source[p - 1] === '/' && source[p] === '*')) p--;
      i = p > floor ? p - 1 : floor;
      continue;
    }
    return i;
  }
}

// Rebuild `parent` so a lateral/apply body sees only aliases (and their reachable
// tables) declared strictly before the frame's `(`. Pseudo-records, CTEs, schema
// tables, currentSchema / currentDatabase, and the grandparent chain (parent's
// own `.parent`) are all preserved unchanged.
export function filterParentForLateral(parent: Scope, frameStart: number): Scope {
  return {
    ...parent,
    aliases: parent.aliases.filter(
      (a) => a.source === 'pseudo' || a.range.startOffset < frameStart,
    ),
    reachableTables: parent.reachableTables.filter(
      (r) => r.via !== 'alias'
        || (r.aliasRef !== undefined && r.aliasRef.range.startOffset < frameStart),
    ),
  };
}
